import React from 'react';

/** Lightweight Markdown → React (bold, italics, headings, lists, hr, paragraphs). */
export function Markdown({ text }: { text: string }) {
  if (!text) return null;
  const blocks = splitBlocks(text.trim());
  return (
    <div className="mai-md">
      {blocks.map((b, i) => {
        if (b.type === 'hr') return <hr key={i} className="mai-md-hr" />;
        if (b.type === 'h2') return <h2 key={i} className="mai-md-h2">{inline(b.text || '')}</h2>;
        if (b.type === 'h3') return <h3 key={i} className="mai-md-h3">{inline(b.text || '')}</h3>;
        if (b.type === 'ul') {
          return (
            <ul key={i} className="mai-md-ul">
              {b.items!.map((it, j) => <li key={j}>{inline(it)}</li>)}
            </ul>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol key={i} className="mai-md-ol">
              {b.items!.map((it, j) => <li key={j}>{inline(it)}</li>)}
            </ol>
          );
        }
        return <p key={i} className="mai-md-p">{inline(b.text || '')}</p>;
      })}
    </div>
  );
}

type Block =
  | { type: 'p' | 'h2' | 'h3' | 'hr'; text: string; items?: undefined }
  | { type: 'ul' | 'ol'; text?: undefined; items: string[] };

function splitBlocks(src: string): Block[] {
  // Defesa: nunca renderizar cercas ```json / JSON de actions
  const cleaned = src
    .replace(/```(?:json)?\s*[\s\S]*?```/gi, '')
    .replace(/```(?:json)?\s*[\s\S]*$/gi, '')
    .replace(/ACTIONS_JSON\s*=\s*\{[\s\S]*$/gi, '')
    .replace(/\{\s*"actions"\s*:\s*\[[\s\S]*$/gi, '')
    .trim();
  const lines = cleaned.replace(/\r\n/g, '\n').split('\n');
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*---+\s*$/.test(line)) {
      out.push({ type: 'hr', text: '' });
      i += 1;
      continue;
    }
    if (/^###\s+/.test(line)) {
      out.push({ type: 'h3', text: line.replace(/^###\s+/, '') });
      i += 1;
      continue;
    }
    if (/^##\s+/.test(line)) {
      out.push({ type: 'h2', text: line.replace(/^##\s+/, '') });
      i += 1;
      continue;
    }
    if (/^\s*[-*•]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*•]\s+/, ''));
        i += 1;
      }
      out.push({ type: 'ul', items });
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i += 1;
      }
      out.push({ type: 'ol', items });
      continue;
    }
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const parts: string[] = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() && !/^#{1,3}\s/.test(lines[i]) && !/^\s*[-*•]\s+/.test(lines[i]) && !/^\s*\d+\.\s+/.test(lines[i]) && !/^\s*---+\s*$/.test(lines[i])) {
      parts.push(lines[i]);
      i += 1;
    }
    out.push({ type: 'p', text: parts.join(' ') });
  }
  return out;
}

function inline(text: string): React.ReactNode {
  // **bold**, *italic*, `code`
  const nodes: React.ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('**')) {
      nodes.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      nodes.push(<em key={key++}>{token.slice(1, -1)}</em>);
    } else {
      nodes.push(<code key={key++} className="mai-md-code">{token.slice(1, -1)}</code>);
    }
    last = m.index + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes.length === 1 ? nodes[0] : <>{nodes}</>;
}
